import React, { useState } from 'react';
import { generateAvatarInitials, generateAvatarColor } from '../../utils/imageUtils';
import { Patient } from '../../types';

interface PatientImageProps {
  patient?: Patient;
  photoUrl?: string;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showFallback?: boolean;
}

const PatientImage: React.FC<PatientImageProps> = ({
  patient,
  photoUrl,
  name,
  size = 'md',
  className = '',
  showFallback = true,
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Get values from patient object if provided, otherwise use props
  const finalPhotoUrl = photoUrl || patient?.photoUrl;
  const finalName = name || patient?.name || 'Unknown';

  // Size classes
  const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-16 w-16 text-lg',
  };

  // Get image URL with fallback logic
  const imageUrl = !imageError && finalPhotoUrl ? finalPhotoUrl : null;
  const showInitials = !imageUrl && showFallback;

  const handleImageError = () => {
    setImageError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  if (showInitials) {
    // Show initials as fallback
    return (
      <div
        className={`
          ${sizeClasses[size]}
          rounded-full
          flex
          items-center
          justify-center
          font-semibold
          text-white
          shadow-soft
          ${className}
        `}
        style={{ backgroundColor: generateAvatarColor(finalName) }}
        title={finalName}
      >
        {generateAvatarInitials(finalName)}
      </div>
    );
  }

  return (
    <div className={`relative ${sizeClasses[size]} ${className}`}>
      {isLoading && (
        <div
          className={`
            ${sizeClasses[size]}
            rounded-full
            bg-slate-100
            animate-pulse
            absolute
            inset-0
          `}
        />
      )}

      <img
        src={imageUrl!}
        alt={finalName}
        className={`
          ${sizeClasses[size]}
          rounded-full
          object-cover
          shadow-soft
          transition-opacity
          duration-200
          ${isLoading ? 'opacity-0' : 'opacity-100'}
        `}
        onError={handleImageError}
        onLoad={handleImageLoad}
        title={finalName}
      />
    </div>
  );
};

export default PatientImage;